/* eslint-disable react/no-array-index-key */
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { DndProvider } from 'react-dnd';
import { DataTableProps } from './types';
import TableHeader from './Header';
import TableRow from './TableRow';
import DraggableTableRow from './DraggableTableRow';
import Spinner from '../spinner';
import { combineClassNames } from '@common/utils/combineClassNames';
import PaginatedTableFooter from './PaginationFooter';
import TableFooter from './Footer';
import dndManager from '@common/utils/dnd-manager';
import { DragScrollController } from './DragScrollController';

const DataTable = ({
  columns,
  showHeader = true,
  data,
  loading,
  pagination,
  className,
  onShowMore,
  onPageChange,
  onPageSizeChange,
  emptyView,
  stickyHeader = false,
  stickyFirstColumn = false,
  maxHeight = '400px',
  sortConfig,
  onSort,
  onRowClick,
  selectedRowId,
  visibleColumnKeys,
  dragAndDrop,
}: DataTableProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const [calculatedHeight, setCalculatedHeight] =
    useState<number | string>(maxHeight);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    pagination?.limit || 10
  );

  const paginationMode = pagination?.mode || 'loadmore';
  const isPaginated = paginationMode === 'paginated';

  const calculateDynamicHeight = useCallback(() => {
    if (!tableRef.current) return;

    const tableElement = tableRef.current;
    const tableRect = tableElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    let paginationFooterHeight = 0;
    if (isPaginated && paginationRef.current) {
      paginationFooterHeight = paginationRef.current.offsetHeight;
    } else if (isPaginated) {
      paginationFooterHeight = 60;
    }
    const baseOffset = 44;

    const availableHeight =
      viewportHeight - tableRect.top - baseOffset - paginationFooterHeight;

    const minHeight = 400;
    const maxHeightLimit = Math.floor(viewportHeight * 0.8);

    const calculatedHeightValue = Math.max(
      minHeight,
      Math.min(availableHeight, maxHeightLimit)
    );

    setCalculatedHeight(calculatedHeightValue);
  }, [isPaginated]);

  useEffect(() => {
    calculateDynamicHeight();

    const handleResize = () => {
      calculateDynamicHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateDynamicHeight]);

  useEffect(() => {
    const timer = setTimeout(() => {
      calculateDynamicHeight();
    }, 100);
    return () => clearTimeout(timer);
  }, [calculateDynamicHeight, data, loading, isPaginated]);

  const tableWrapperClasses = [
    // Callers style this same element through `className`, and several add
    // padding and a border to make it the table's card. Tailwind preflight is
    // off and nothing sets a global border-box, so without this `tw-w-full`
    // would measure the content box only and the card would sit its own
    // padding + border wider than the column it lives in — which is the
    // horizontal scrollbar that shows up under tables whose columns fit.
    'tw-box-border tw-w-full',
    stickyHeader || stickyFirstColumn ? 'tw-overflow-auto' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const tableClasses = [
    // No tw-divide-y: header/rows/footer already carry their own dividers, and
    // the extra section border stacks into a double line.
    'tw-table-fixed tw-min-w-full tw-border-spacing-0',
    stickyHeader ? 'tw-sticky-header' : '',
    stickyFirstColumn ? 'tw-sticky-first-column' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const finalHeight = calculatedHeight;

  const getMaxHeightValue = () => {
    if (finalHeight === 'auto') {
      return undefined;
    }
    if (typeof finalHeight === 'number') {
      return `${finalHeight}px`;
    }
    return finalHeight;
  };

  const getOverflowYValue = () => {
    if (finalHeight === 'auto') {
      return 'visible';
    }
    return 'auto';
  };

  const tableWrapperStyle: React.CSSProperties | undefined =
    stickyHeader || stickyFirstColumn
      ? {
          maxHeight: getMaxHeightValue(),
          overflowY: getOverflowYValue(),
          overflowX: 'auto',
        }
      : undefined;

  const totalCount = useMemo(
    () => pagination?.total_count || data.length || 0,
    [pagination?.total_count, data.length]
  );
  const startIndex = useMemo(() => page * rowsPerPage, [page, rowsPerPage]);
  const endIndex = useMemo(() => (page + 1) * rowsPerPage, [page, rowsPerPage]);

  const slicedData = useMemo(() => {
    if (!pagination) return data;
    if (isPaginated) return data.slice(startIndex, endIndex);

    return data;
  }, [data, startIndex, endIndex, pagination, isPaginated]);

  const useCursor = Boolean(pagination?.useCursor);
  const hasPrev = useMemo(() => page > 0, [page]);
  const hasNext = useMemo(() => {
    if (useCursor) {
      const moreBuffered = endIndex < data.length;
      const moreServer = Boolean((pagination as any)?.next_cursor);
      return moreBuffered || moreServer;
    }
    return endIndex < totalCount;
  }, [useCursor, endIndex, totalCount, data.length, pagination]);

  useEffect(() => {
    setPage(0);
    if (pagination?.limit) setRowsPerPage(pagination.limit);
  }, [totalCount, pagination?.limit]);

  const handleNext = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    const need = (nextPage + 1) * rowsPerPage;

    const serverHasMore = pagination
      ? pagination.useCursor
        ? Boolean((pagination as any)?.next_cursor)
        : pagination.useOffset
        ? (pagination.total_count || 0) >
          (pagination.offset || 0) + (pagination.limit || rowsPerPage)
        : false
      : false;

    if (
      pagination &&
      typeof onShowMore === 'function' &&
      data.length < need &&
      serverHasMore
    ) {
      onShowMore();
    }
    onPageChange?.(nextPage, rowsPerPage);
  }, [page, rowsPerPage, pagination, data.length, onShowMore]);

  const handlePrev = useCallback(() => {
    setPage((p) => {
      const newPage = Math.max(0, p - 1);
      onPageChange?.(newPage, rowsPerPage);
      return newPage;
    });
  }, [rowsPerPage, onPageChange]);

  const handleRowsPerPageChange = useCallback(
    (value: number) => {
      setRowsPerPage(value);
      setPage(0);
      onPageSizeChange?.(value);
      onPageChange?.(0, value);
    },
    [onPageSizeChange, onPageChange]
  );

  const visibleColumns = useMemo(() => {
    if (!visibleColumnKeys || visibleColumnKeys.length === 0) return columns;

    const byKey = new Map(columns.map((column) => [column.key, column]));
    const ordered = visibleColumnKeys
      .map((key) => byKey.get(key))
      .filter((column): column is typeof columns[number] => !!column);

    return ordered.length ? ordered : columns;
  }, [columns, visibleColumnKeys]);

  const renderTableBody = () => {
    if (loading && !pagination) {
      return (
        <tr>
          <td
            colSpan={visibleColumns.length}
            className="tw-text-center tw-py-4 tw-px-2"
          >
            <Spinner centered />
          </td>
        </tr>
      );
    }

    if (data.length === 0 && !loading) {
      return (
        <tr>
          <td
            colSpan={visibleColumns.length}
            className="tw-text-center tw-py-4 tw-px-2"
          >
            {emptyView}
          </td>
        </tr>
      );
    }

    const rows = pagination ? slicedData : data;
    const hasLoadMoreFooter = Boolean(
      pagination &&
      paginationMode === 'loadmore' &&
      (
        loading ||
        (pagination.useCursor ? Boolean((pagination as any)?.next_cursor) : (
          pagination.useOffset
            ? (pagination.total_count || 0) > (pagination.offset || 0) + (pagination.limit || rowsPerPage)
            : false
        ))
      )
    );

    // Use DraggableTableRow when drag and drop is enabled
    if (dragAndDrop?.enabled) {
      return rows.map((row, index) => (
        <DraggableTableRow
          columns={visibleColumns}
          row={row}
          index={index}
          key={`datatable-row-${row?.id || index}`}
          stickyFirstColumn={stickyFirstColumn}
          onRowClick={onRowClick}
          selectedRowId={selectedRowId}
          onChangeOrder={dragAndDrop.onChangeOrder}
          onDrop={dragAndDrop.onDrop}
          dragType={dragAndDrop.dragType}
          dragEnabled={dragAndDrop.enabled}
          isFirstRow={!showHeader && index === 0}
          isLastRow={index === rows.length - 1 && !hasLoadMoreFooter && !isPaginated}
        />
      ));
    }

    return rows.map((row, index) => (
      <TableRow
        columns={visibleColumns}
        row={row}
        key={`datatable-row-${row?.id || index}`}
        stickyFirstColumn={stickyFirstColumn}
        onRowClick={onRowClick}
        selectedRowId={selectedRowId}
        isFirstRow={!showHeader && index === 0}
        isLastRow={index === rows.length - 1 && !hasLoadMoreFooter && !isPaginated}
      />
    ));
  };

  const tableContent = (
    <>
      <div
        ref={tableRef}
        className={combineClassNames(tableWrapperClasses, className)}
        style={tableWrapperStyle}
      >
        <table className={tableClasses}>
          {showHeader && (
            <TableHeader
              columns={visibleColumns}
              stickyHeader={stickyHeader}
              stickyFirstColumn={stickyFirstColumn}
              sortConfig={sortConfig}
              onSort={onSort}
            />
          )}
          <tbody>{renderTableBody()}</tbody>
          {pagination && paginationMode === 'loadmore' && (
            <TableFooter
              loading={loading}
              columns={visibleColumns}
              onShowMore={onShowMore}
              pagination={pagination}
            />
          )}
        </table>
      </div>
      {pagination && paginationMode === 'paginated' && (
        <div ref={paginationRef}>
          <PaginatedTableFooter
            loading={loading}
            onShowMore={onShowMore}
            pagination={pagination}
            page={page}
            rowsPerPage={rowsPerPage}
            onPrev={handlePrev}
            onNext={handleNext}
            onRowsPerPageChange={handleRowsPerPageChange}
            hasPrev={hasPrev}
            hasNext={hasNext}
            totalCount={totalCount}
          />
        </div>
      )}
    </>
  );

  // Wrap with DndProvider when drag and drop is enabled
  if (dragAndDrop?.enabled) {
    return (
      <DndProvider manager={dndManager}>
        <DragScrollController
          tableRef={tableRef}
          dragType={dragAndDrop.dragType}
          scrollEdgeSize={dragAndDrop.scrollEdgeSize}
        />
        {tableContent}
      </DndProvider>
    );
  }

  return tableContent;
};

export default DataTable;
